/**
 * checklists.js — Firestore for the standing checklists (Shopping · Backup · Cutting).
 *
 * Two collections:
 *   opsChecklistItems/{id}  — the persistent par-check items
 *     { list, name, store, qty, needed, done, neededAt, order, lastDoneAt, hidden, addedBy, createdAt }
 *     `hidden` = deleted from the checklist (✕). Kept as a doc so the name doesn't come
 *     back as a history-only entry; adding the name again un-hides it.
 *   opsChecklistLog/{id}    — one row per add→complete CYCLE (history/tracking)
 *     { list, name, qty, store, addedDate, date:'YYYY-MM-DD'(completed), status, by, at }
 *   opsLists/_deleted       — { checklistLog: { <rowId>: 'YYYY-MM-DD' } } tombstones of
 *     deleted history rows (see removeChecklistLog)
 *
 * Items are standing (not per-day). Adding an item to the active list stamps its
 * `neededAt` (when this cycle started); "completing" it logs a dated row that snapshots
 * both the added date and the completion date, then clears it so the item can be added
 * again — each cycle is its own history row. All queries are single-field → no
 * composite index to deploy.
 */
import {
  collection, doc, query, where, orderBy, limit,
  onSnapshot, getDocsFromServer, setDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch, deleteField,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { daysAgoISO, localDateISO } from '../logic/dates';
import { shareFeed } from './liveFeed';
import { rowTracker } from './rows';
import { debugLog, snapInfo } from './debug';
import {
  CHECKLIST_COLLECTION, CHECKLIST_LOG_COLLECTION, LIVE_WINDOW_DAYS, RECENT_QUERY_LIMIT,
  LOG_HISTORY_LIMIT, LOG_SYNC_KEY, LISTS_COLLECTION, DELETED_DOC_ID,
} from '../config/constants';

const itemRef = id => doc(db, CHECKLIST_COLLECTION, id);
const logRef = id => doc(db, CHECKLIST_LOG_COLLECTION, id);
const tombstonesRef = () => doc(db, LISTS_COLLECTION, DELETED_DOC_ID);

// ── Items (one shared, lingering listener per checklist — see liveFeed.js) ───
/**
 * Live items of one checklist (sorted client-side to avoid a composite index). `meta.fromCache`
 * says whether the server has confirmed them yet (an empty list only counts once it has).
 */
export function subscribeChecklistItems(listId, onData, onError) {
  const key = `${CHECKLIST_COLLECTION}:${listId}`;
  return shareFeed(key, (emit, fail) => {
    const rowsOf = rowTracker();
    return onSnapshot(query(collection(db, CHECKLIST_COLLECTION), where('list', '==', listId), limit(500)), snap => {
      debugLog('snapshot', key, snapInfo(snap));
      emit(rowsOf(snap), { fromCache: !!(snap.metadata && snap.metadata.fromCache) });
    }, fail);
  }, onData, onError);
}

// ── Completion log (all three checklists share ONE feed) ─────────────────────
// It used to be a `list == X, limit(1000)` listener per tab: up to 1,000 reads every
// time a tab opened after a quiet spell, and — with no order — an arbitrary 1,000 rows
// once a log grew past that. Log rows are written once and never edited (an undo only
// deletes), so each day only has to reach a device once:
//   • live    — the last LIVE_WINDOW_DAYS from the server (new rows pushed);
//   • history — everything older from the device's own Firestore cache (no reads);
//   • catch-up — on open, the server is asked only for the days this device hasn't
//     seen since it was last here (LOG_SYNC_KEY = { through }); a new device (or a
//     cleared cache) fetches the whole history once.
// One shared feed serves Shopping, Backup, Cutting and the Summary; each takes its rows.
const logQuery = (from, before, cap) => {
  const range = [];
  if (from) range.push(where('date', '>=', from));
  if (before) range.push(where('date', '<', before));
  return query(collection(db, CHECKLIST_LOG_COLLECTION), ...range, orderBy('date', 'desc'), limit(cap));
};

/** Live completion log of one checklist, newest first. */
export function subscribeChecklistLog(listId, onData, onError) {
  const liveFrom = daysAgoISO(LIVE_WINDOW_DAYS);
  let last = null;
  const mine = rows => {
    const own = rows.filter(r => r.list === listId);
    // Rows keep their identity (rowTracker), so an update for another list changes nothing here.
    if (last && last.length === own.length && last.every((r, i) => r === own[i])) return;
    last = own;
    onData(own);
  };
  return shareFeed(`${CHECKLIST_LOG_COLLECTION}:all:${liveFrom}`,
    (emit, fail) => openLogFeed(liveFrom, emit, fail), mine, onError);
}

function openLogFeed(liveFrom, emit, fail) {
  let live = null;
  let past = null;
  let closed = false;
  const publish = () => { if (!closed && live && past) emit(live.concat(past)); };

  const liveRows = rowTracker();
  const stopLive = onSnapshot(logQuery(liveFrom, null, RECENT_QUERY_LIMIT), snap => {
    if (closed) return;
    debugLog('snapshot', `${CHECKLIST_LOG_COLLECTION}:live:${liveFrom}`, snapInfo(snap));
    live = liveRows(snap);
    publish();
  }, err => {
    if (closed) return;
    close();
    fail(err);
  });

  const pastRows = rowTracker();
  const stopPast = onSnapshot(logQuery(null, liveFrom, LOG_HISTORY_LIMIT), { source: 'cache' }, snap => {
    if (closed) return;
    debugLog('snapshot', `${CHECKLIST_LOG_COLLECTION}:history<${liveFrom}`, snapInfo(snap));
    const first = past === null;
    past = pastRows(snap);
    if (first) catchUpLog(liveFrom, past.length);
    publish();
  }, () => {
    if (closed || past) return;
    past = [];
    catchUpLog(liveFrom, 0);
    publish();
  });

  function close() {
    closed = true;
    stopLive();
    stopPast();
  }
  return close;
}

/**
 * Fetch, into the local cache, the log days this device is missing before `liveFrom`:
 * from where its last catch-up reached, or all of it the first time (or when the cache
 * came back empty). The cache-only listener then shows them.
 */
function catchUpLog(liveFrom, cachedCount) {
  let through = '';
  try { through = (JSON.parse(localStorage.getItem(LOG_SYNC_KEY) || 'null') || {}).through || ''; } catch { /* unreadable → all */ }
  if (cachedCount === 0) through = '';
  if (through && through >= liveFrom) return;
  getDocsFromServer(logQuery(through || null, liveFrom, LOG_HISTORY_LIMIT))
    .then(snap => {
      debugLog('catch-up', `${CHECKLIST_LOG_COLLECTION}:${through || '…'}..${liveFrom}`, `${snap.size} docs (server)`);
      try { localStorage.setItem(LOG_SYNC_KEY, JSON.stringify({ through: liveFrom })); } catch { /* try again next time */ }
    })
    .catch(() => { /* offline or over quota: the next open tries again */ });
}

// ── Writes ───────────────────────────────────────────────────────────────────
// Several docs changed by one tap go in ONE batch: one round trip, and one security-rules
// check (each check reads users/{uid} — a billed read) instead of one per doc.
// New docs get their id on the device, so an undo can name them before the server answers.

const newChecklistItem = ({ list, name, store = '', qty = '', order = 0, needed = false, hidden = false, addedBy = '', today }) => ({
  list,
  name: String(name || '').trim(),
  store: String(store || '').trim(),   // which shop to buy it from
  qty: String(qty || '').trim(),       // free-text amount ("2 crates", "5 lb") — snapshotted into each log row
  order,
  needed: !!needed,   // true = on the active "to buy/cut" list; false = just in the catalog
  done: false,        // true = checked off (bought/cut) but not yet moved to history
  neededAt: needed ? (today || localDateISO()) : null,   // when this cycle went onto the active list
  lastDoneAt: null,
  hidden: !!hidden,   // true = deleted from the checklist (see the header)
  addedBy,
  createdAt: serverTimestamp(),
});

/**
 * Create one catalog item (on the active list when `needed`; `hidden` = born deleted, to
 * hide a history-only name). @returns {{ id, saved }}
 */
export function addChecklistItem(fields) {
  const ref = doc(collection(db, CHECKLIST_COLLECTION));
  return { id: ref.id, saved: setDoc(ref, newChecklistItem(fields)) };
}

/**
 * Put names on the active list in one write — the plan from planChecklistAdds:
 * `creates` become new items (ordered from `firstOrder` in steps of 10), `updates`
 * re-flag existing ones. @returns {{ createdIds, saved }} — the new ids are for an undo.
 */
export function addToChecklist({ list, creates = [], updates = [], firstOrder = 10, addedBy = '', today }) {
  const batch = writeBatch(db);
  const createdIds = creates.map((c, i) => {
    const ref = doc(collection(db, CHECKLIST_COLLECTION));
    batch.set(ref, newChecklistItem({ list, name: c.name, store: c.store, order: firstOrder + i * 10, needed: true, addedBy, today }));
    return ref.id;
  });
  updates.forEach(u => batch.update(itemRef(u.id), u.patch));
  return { createdIds, saved: batch.commit() };
}

/**
 * Undo of addToChecklist: delete what it created, take re-flagged items back off the list —
 * and those that had been deleted from the checklist (`rehideIds`) back out of it.
 */
export function undoAddToChecklist({ createdIds = [], unflagIds = [], rehideIds = [] }) {
  const batch = writeBatch(db);
  const rehide = new Set(rehideIds);
  createdIds.forEach(id => batch.delete(itemRef(id)));
  unflagIds.forEach(id => batch.update(itemRef(id), rehide.has(id) ? { needed: false, done: false, hidden: true } : { needed: false, done: false }));
  return batch.commit();
}

/**
 * Put an item on (or take it off) the active list. Adding stamps the cycle's start
 * date (`neededAt`) and may fill its store in the same write; removing also clears its check.
 */
export function setChecklistNeeded(id, needed, { today, store } = {}) {
  const patch = needed ? { needed: true, neededAt: today || localDateISO() } : { needed: false, done: false };
  if (needed && store) patch.store = store;
  return updateDoc(itemRef(id), patch);
}

export function updateChecklistItem(id, patch) {
  return updateDoc(itemRef(id), patch);
}

/**
 * Delete an item from the checklist (the catalog's ✕): hidden, and off the active list.
 * Its history stays; adding the name again brings it back (planChecklistAdds).
 */
export function hideChecklistItem(id) {
  return updateDoc(itemRef(id), { hidden: true, needed: false, done: false });
}
/** Undo of hideChecklistItem — `prev` = the item as it was ({ needed, done }). */
export function unhideChecklistItem(id, prev = {}) {
  return updateDoc(itemRef(id), { hidden: false, needed: !!prev.needed, done: !!prev.done });
}

/** Remove an item doc outright (undo of hiding a history-only name: the doc made for it). */
export function removeChecklistItem(id) {
  return deleteDoc(itemRef(id));
}

/**
 * Archive several items to history at once and clear them from the active list.
 * Each logs its outcome: checked (`done`) → 'completed', unchecked → 'pending'
 * (only 'completed' stamps lastDoneAt). One batch.
 * @returns {{ logIds, saved }} — the new log rows, so an undo can delete them (no orphaned history)
 */
export function completeChecklistItems(items, { date, by = '' }) {
  const batch = writeBatch(db);
  const logIds = [];
  (items || []).forEach(item => {
    const completed = !!item.done;
    const ref = doc(collection(db, CHECKLIST_LOG_COLLECTION));
    logIds.push(ref.id);
    batch.set(ref, {
      list: item.list, name: item.name, qty: item.qty || '', store: item.store || '',
      addedDate: item.neededAt || '',   // when this cycle was added (snapshot)
      status: completed ? 'completed' : 'pending',
      date, by, at: serverTimestamp(),   // `date` = completion date
    });
    batch.update(
      itemRef(item.id),
      completed ? { lastDoneAt: date, needed: false, done: false } : { needed: false, done: false },
    );
  });
  return { logIds, saved: batch.commit() };
}

/** Undo of completeChecklistItems: delete the log rows and put the items back on the list. */
export function reopenChecklistItems({ itemIds = [], logIds = [] }) {
  const batch = writeBatch(db);
  logIds.forEach(id => batch.delete(logRef(id)));
  itemIds.forEach(id => batch.update(itemRef(id), { needed: true }));
  return batch.commit();
}

/**
 * Delete one history row (the ✕ in the log). Rows older than the live week reach other
 * devices only once and then come from their own cache, so a plain delete would never
 * reach them — the same batch leaves a tombstone in opsLists/_deleted, which every device
 * already listens to (hooks/useChecklist.js drops tombstoned rows).
 */
export function removeChecklistLog(row) {
  const batch = writeBatch(db);
  batch.delete(logRef(row.id));
  batch.set(tombstonesRef(), { checklistLog: { [row.id]: localDateISO() } }, { merge: true });
  return batch.commit();
}
/** Undo of removeChecklistLog: the row back exactly as it was (same id), tombstone gone. */
export function restoreChecklistLog(row) {
  const { id, ...data } = row;
  const batch = writeBatch(db);
  batch.set(logRef(id), data);
  batch.set(tombstonesRef(), { checklistLog: { [id]: deleteField() } }, { merge: true });
  return batch.commit();
}

/** Seed a checklist's item template (only when the server confirms it's empty — useChecklist). */
export function seedChecklist(listId, names) {
  const batch = writeBatch(db);
  (names || []).forEach((name, i) => {
    batch.set(doc(collection(db, CHECKLIST_COLLECTION)), newChecklistItem({ list: listId, name, order: (i + 1) * 10, addedBy: 'seed' }));
  });
  return batch.commit();
}
